<template>
    <section
        ref="dropdownRef"
        name="input-select"
        class="gap-xs flex flex-col"
        :class="{ 'pointer-events-none': props.disabled }"
    >
        <div v-if="label || required" class="gap-xs flex">
            <span v-if="required" class="text-error">*</span>
            <label>{{ label }}</label>
        </div>
        <div :id="'input-select-parent-' + uniqueKey" class="relative rounded-sm">
            <div id="input-select">
                <div
                    ref="inputWrapperRef"
                    class="input-wrapper rotate-wrapper pr-2.5"
                    :class="{
                        'hide-placeholder': isDropdownOpen,
                        'rounded-b-none': isDropdownOpen && !placeAbove,
                        'rounded-t-none': isDropdownOpen && placeAbove,
                        'active-parent': isParentActive || isDropdownOpen,
                        'custom-dropdown': value.length > 0,
                        'input-error': errorMessage,
                    }"
                    @focusin="handleInputFocus"
                    @focusout="handleInputBlur"
                >
                    <input
                        :id="'input-select-' + uniqueKey"
                        ref="inputRef"
                        v-model="searchQuery"
                        type="search"
                        class="select-selected relative text-ellipsis"
                        :placeholder="
                            value.length > 0
                                ? getValueString(value).charAt(0).toUpperCase() + getValueString(value).slice(1)
                                : placeholder.charAt(0).toUpperCase() + placeholder.slice(1)
                        "
                        autocomplete="off"
                        :disabled="props.disabled"
                        @input="handleInput"
                    />
                    <span v-if="value?.[0]?.placeholderClass" :class="value[0].placeholderClass">
                        {{
                            value.length > 0
                                ? getValueString(value).charAt(0).toUpperCase() + getValueString(value).slice(1)
                                : placeholder
                        }}
                    </span>
                    <i class="i-small-arrow-down" :class="{ flipped: isDropdownOpen }" @click="toggleDropdown" />
                </div>
                <teleport v-if="teleportTarget" :to="teleportTarget">
                    <div
                        v-show="isDropdownOpen"
                        :id="'options-' + uniqueKey"
                        ref="actualDropdownRef"
                        class="options bg-lightest absolute z-10 max-h-100 w-full overflow-y-auto border-1"
                        :class="{
                            'dropdown-open': isDropdownOpen,
                            'rounded-t-sm border-b-0': isDropdownOpen && placeAbove,
                            'rounded-b-sm border-t-0': isDropdownOpen && !placeAbove,
                        }"
                        :style="dropdownStyles"
                    >
                        <div
                            v-for="(option, index) in filteredOptions"
                            :key="option.value"
                            :class="{
                                'font-bold': isSelected(option), //value.some((value) => value.value === option.value),
                                focused: focusedOptionIndex === index,
                            }"
                            class="option gap-xs transition-color px-xs py-sm flex rounded-sm duration-300 ease-linear hover:bg-(--surface-highlight) hover:font-normal"
                            :tabindex="0"
                            @click.stop="selectOption(option)"
                        >
                            <input v-if="multiselect" type="checkbox" :checked="isSelected(option)" />
                            <p :class="option.dropdownClass" class="first-letter:uppercase">
                                {{ option.label }}
                            </p>
                        </div>
                    </div>
                </teleport>
            </div>
        </div>
        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    </section>
</template>

<script setup lang="ts">
import type { Option } from '@/stores/store-interfaces';
import {
    ref,
    computed,
    onMounted,
    watch,
    nextTick,
    inject,
    type Ref,
    reactive,
    onBeforeUnmount,
    type StyleValue,
} from 'vue';

const emit = defineEmits(['update']);
const portalContainerRef = inject<Ref<HTMLElement | null>>('portalContainerRef');
const teleportTarget = ref<HTMLElement | null>(null);
const dropdownStyles: StyleValue = reactive({
    position: 'absolute' as const,
    top: '0px',
    left: '0px',
    width: 'auto',
});

const isDropdownOpen = ref(false);
const placeAbove = ref(true);
const searchQuery = ref('');
// Reference to the component DOM element
const inputRef = ref<HTMLElement | null>(null);
const inputWrapperRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const actualDropdownRef = ref<HTMLElement | null>(null);
const isParentActive = ref(false);
const isInputFocused = ref(false);
const focusedOptionIndex = ref(-1);

const props = defineProps({
    value: {
        type: Array as () => Option[],
        required: true,
    },
    placeholder: {
        type: String,
        default: 'Select',
    },
    options: {
        type: Array as () => Option[],
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        default: false,
    },
    multiselect: {
        type: Boolean,
        default: false,
    },
    uniqueKey: {
        type: Number,
        required: true,
    },
    errorMessage: {
        type: String,
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    emptyable: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const selectedValues = ref<Option[]>(props.value);

function isSelected(option: Option) {
    return selectedValues.value.some((item) => item.value === option.value);
}

const filteredOptions = computed(() => {
    if (!searchQuery.value) return props.options;
    return props.options.filter((option) => option.label?.toLowerCase().includes(searchQuery.value.toLowerCase()));
});

async function toggleDropdown() {
    isDropdownOpen.value = !isDropdownOpen.value;
    if (searchQuery.value !== '') {
        searchQuery.value = '';
    }
    if (isDropdownOpen.value) {
        inputRef.value?.focus(); // 👈 focus the input when opening
    }

    updateDropdownPositionIfNeeded();
}

const updateDropdownPositionIfNeeded = async () => {
    if (isDropdownOpen.value) {
        await nextTick();
        updateDropdownPosition();
    }
};

const updateDropdownPosition = () => {
    if (!portalContainerRef?.value) {
        console.warn('portalContainerRef missing', portalContainerRef?.value);
        return;
    }
    const portalRect = portalContainerRef?.value?.parentElement?.getBoundingClientRect();
    if (!inputWrapperRef.value || !actualDropdownRef.value || !portalRect) {
        console.warn(
            'inputWrapperRef or actualDropdownRef missing',
            inputWrapperRef.value,
            actualDropdownRef.value,
            portalContainerRef?.value,
        );
        return;
    }
    const portalLeft = (portalRect?.left || 0) + (portalContainerRef.value?.parentElement?.clientLeft || 0);
    const portalTop = (portalRect?.top || 0) + (portalContainerRef.value?.parentElement?.clientTop || 0);

    const inputRect = inputWrapperRef.value.getBoundingClientRect();

    const dropdownHeight = actualDropdownRef.value.offsetHeight;

    const spaceBelow = portalRect.bottom - inputRect.bottom;
    const spaceAbove = inputRect.top - portalRect.top;
    // drawDebugBoundingBox(inputRect.top, inputRect.left, inputRect.bottom, inputRect.right, 'green');
    // drawDebugBoundingBox(portalRect.top, portalRect.left, portalRect.bottom, portalRect.right, 'pink');

    placeAbove.value = dropdownHeight > spaceBelow && spaceAbove > dropdownHeight; // TODO: account for border width? clientLeft and clientTop

    const offsetLeft = inputRect.left - portalLeft;
    dropdownStyles.left = `${offsetLeft}px`;

    if (placeAbove.value) {
        // const offsetBottom = portalRect.top + inputRect.top;
        // const offsetBottom = inputRect.top - portalRect.top - dropdownHeight - portalContainerRef.value.clientTop;
        dropdownStyles.top = 'auto';
        // dropdownStyles.bottom = `${offsetBottom}px`;

        dropdownStyles.bottom = `${portalTop - inputRect.top}px`;

        // dropdownStyles.bottom = `${portalRect.bottom - inputRect.top + portalContainerRef.value.clientTop}px`;
    } else {
        const offsetTop = inputRect.bottom - portalTop - portalContainerRef.value.clientTop;
        dropdownStyles.top = `${offsetTop}px`;
        dropdownStyles.bottom = 'auto';
    }

    dropdownStyles.width = `${inputRect.width}px`;
};

function selectOption(option: Option) {
    if (props.multiselect) {
        const index = selectedValues.value.findIndex((value) => value.value === option.value);
        if (index === -1) {
            selectedValues.value.push(option);
        } else {
            selectedValues.value.splice(index, 1);
        }
        nextTick(() => {
            inputWrapperRef.value?.focus();
        });
    } else {
        selectedValues.value = [option];
    }
    emit('update', selectedValues.value);
}

function getValueString(values: Option[]) {
    return values.length > 0 ? values.map((value) => value.label).join(', ') : '';
}

watch(selectedValues, (newValues) => {
    if (newValues.length > 0) {
        searchQuery.value = newValues
            .map((value) => props.options.find((option) => option === value)?.label)
            .join(', ');
    } else {
        searchQuery.value = '';
    }
    isDropdownOpen.value = false; // Close dropdown after selection
});

onMounted(() => {
    console.log('mounted!');
    // teleport
    teleportTarget.value = portalContainerRef?.value ?? null;
    window.addEventListener('resize', updateDropdownPositionIfNeeded); //

    //
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);

    if (props.options.length === 1 && !props.emptyable) {
        selectedValues.value = [props.options[0]];
        emit('update', selectedValues.value);
    }

    const parent = document.getElementById(`input-select-parent-${props.uniqueKey}`);
    const options = document.getElementById(`options-${props.uniqueKey}`);
    if (parent) {
        parent.addEventListener('mouseenter', handleParentHover);
        parent.addEventListener('mouseleave', handleParentLeave);
        // parent.addEventListener('active', handleParentHover);
        parent.addEventListener('focusin', handleParentHover);
        parent.addEventListener('focusout', handleParentLeave);
        parent.addEventListener('mousedown', handleParentHover);
    }
    if (options) {
        options.addEventListener('mouseenter', handleChildHover);
        options.addEventListener('mouseleave', handleChildLeave);
        options.addEventListener('focusin', handleChildHover);
        options.addEventListener('focusout', handleChildLeave);
        options.addEventListener('mousedown', handleChildHover);
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateDropdownPositionIfNeeded); //

    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleKeydown);
    const parent = document.getElementById(`input-select-parent-${props.uniqueKey}`);
    const options = document.getElementById(`options-${props.uniqueKey}`);
    if (parent) {
        parent.removeEventListener('mouseenter', handleParentHover);
        parent.removeEventListener('mouseleave', handleParentLeave);
        parent.removeEventListener('focusin', handleParentHover);
        parent.removeEventListener('focusout', handleParentLeave);
        parent.removeEventListener('mousedown', handleParentHover);
    }
    if (options) {
        options.removeEventListener('mouseenter', handleChildHover);
        options.removeEventListener('mouseleave', handleChildLeave);
        options.removeEventListener('focusin', handleChildHover);
        options.removeEventListener('focusout', handleChildLeave);
        options.removeEventListener('mousedown', handleChildHover);
    }
});

function handleInput() {
    isDropdownOpen.value = true; // Keep dropdown open while typing
}

function handleClickOutside(event: MouseEvent) {
    if (!dropdownRef.value) return;
    if (!dropdownRef.value.contains(event.target as Node)) {
        isDropdownOpen.value = false;
    }
}

function handleParentHover() {
    isParentActive.value = true;
}

function handleParentLeave() {
    if (!isInputFocused.value && !isDropdownOpen.value) {
        isParentActive.value = false;
    }
}

function handleInputFocus() {
    isInputFocused.value = true;
    isParentActive.value = true;
    isDropdownOpen.value = true;
    searchQuery.value = ''; // Clear search query on focus

    updateDropdownPositionIfNeeded();
}

function handleInputBlur(event: FocusEvent) {
    isInputFocused.value = false;

    // Remove active class only if the mouse is not over the parent
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!dropdownRef.value?.contains(relatedTarget)) {
        // TODO
        isParentActive.value = false;
    }
}

function handleChildHover() {
    isParentActive.value = true;
}
function handleChildLeave() {
    isParentActive.value = false;
}

function handleKeydown(event: KeyboardEvent) {
    if (!isDropdownOpen.value) return;

    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault();
            if (focusedOptionIndex.value < filteredOptions.value.length - 1) {
                focusedOptionIndex.value++;
            }
            break;
        case 'ArrowUp':
            event.preventDefault();
            if (focusedOptionIndex.value > 0) {
                focusedOptionIndex.value--;
            }
            break;
        case 'Enter':
            event.preventDefault();
            if (focusedOptionIndex.value >= 0) {
                selectOption(filteredOptions.value[focusedOptionIndex.value]);
            }
            break;
        case 'Tab':
            if (focusedOptionIndex.value < filteredOptions.value.length - 1) {
                event.preventDefault();
                focusedOptionIndex.value++;
            } else {
                isDropdownOpen.value = false; // Close dropdown on tab at the last index
            }
            break;
        case 'Escape':
            isDropdownOpen.value = false; // Close dropdown on escape
            break;
    }
}

watch(focusedOptionIndex, (newIndex) => {
    const optionsContainer = document.getElementById(`options-${props.uniqueKey}`);
    const optionElements = optionsContainer?.querySelectorAll('.option') || [];
    if (optionElements[newIndex]) {
        optionElements[newIndex].scrollIntoView({ block: 'nearest' });
    }
});

watch(filteredOptions, () => {
    focusedOptionIndex.value = -1;
});

watch(
    () => props.value,
    (newValue) => {
        if (props.emptyable) selectedValues.value = newValue;
    },
);
</script>

<style scoped>
@reference '@/assets/styles/default.css';
.options {
    @apply px-sm py-xs flex flex-col;
}

.option.focused {
    @apply bg-blue-100;
}

.dropdown-open {
    @apply border-blue-500;
    box-shadow: 0 6px 6px 0 rgba(0, 0, 0, 0.03);
}

.custom-dropdown {
    input::placeholder {
        @apply text-dark-500;
    }
}
.hide-placeholder {
    input::placeholder {
        @apply text-gray-200;
    }
}
</style>
